import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { debounce } from 'lodash';
import SentimentMarker from 'js/components/Icons/SentimentMarker';
import { Icon } from 'js/components';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faEmptySet } from '@fortawesome/pro-regular-svg-icons';
import { standardColor } from 'js/utils/color';
import { FIELD_DELIMITER } from 'js/utils/string';
import {
  decoratedOptions,
  isLiveSentimentQuestion,
  resolveLiveQuestionType,
} from 'js/utils/sentiment-question';
import { NA } from 'js/utils/question-set';
import {
  isBlankComment,
  liveNaChoice,
  orderedLiveChoices,
  selectedLiveChoiceValues,
} from '../utilities';

const preventDefault = (e) => e.preventDefault();
const SINGLE_SELECT_COLOR = standardColor();

/**
 * These no longer carry any color -- that comes from `--response-color` -- but
 * they remain the only stable way to address a specific option, which the e2e
 * suite relies on to cast a vote.
 */
const PERFORMING_CLASS = {
  red: 'performing-poorly',
  yellow: 'performing-middle',
  green: 'performing-well',
};

function sameValue(left, right) {
  return String(left) === String(right);
}

function ResponseRadio() {
  return (
    <span className="decorator response-icon" aria-hidden="true">
      <span className="decorator-dot" />
    </span>
  );
}

function ResponseCheck() {
  return (
    <span className="decorator response-icon" aria-hidden="true">
      <Icon icon={faCheck} className="decorator-check" />
    </span>
  );
}

function NaOption({
  naChoice,
  naChosen,
  response,
  isMarker,
  isSentiment,
  radioName,
  onVote,
  handleVote,
}) {
  const classNames = {
    chosen: naChosen,
    'not-chosen': response && !naChosen,
  };
  const label = naChoice.text || 'N/A';
  const key = naChoice.id ?? naChoice.guid ?? 'na';

  if (isSentiment) {
    const Tag = isMarker ? 'button' : 'div';
    return (
      <Tag
        key={key}
        className={cn(
          'question-response',
          'question-response-plain',
          classNames
        )}
        {...(isMarker
          ? {
              type: 'button',
              onMouseDown: preventDefault,
              onClick: (e) => handleVote(e, NA),
            }
          : {})}
      >
        <span className="decorator response-icon" aria-hidden="true">
          <Icon icon={faEmptySet} />
        </span>
        <div>{label}</div>
      </Tag>
    );
  }

  const Tag = isMarker ? 'label' : 'div';
  return (
    <Tag
      key={key}
      className={cn(
        'question-response',
        'question-response-plain',
        'custom-radio',
        classNames
      )}
    >
      {isMarker ? (
        <input
          type="radio"
          name={radioName}
          value={NA}
          checked={naChosen}
          onChange={() => onVote(NA)}
        />
      ) : null}
      <ResponseRadio />
      <div>{label}</div>
    </Tag>
  );
}

function LiveComment({
  question,
  response,
  onVote,
  onUnvote,
  isMarker,
  children,
}) {
  const [value, setValue] = useState(() => response?.value ?? '');
  const recordedRef = useRef(response?.value ?? '');
  const hasSavedRef = useRef(Boolean(response?.id));
  const onVoteRef = useRef(onVote);
  const onUnvoteRef = useRef(onUnvote);
  onVoteRef.current = onVote;
  onUnvoteRef.current = onUnvote;

  const flush = useCallback((next) => {
    if (isBlankComment(next)) {
      if (!hasSavedRef.current) {
        return;
      }
      hasSavedRef.current = false;
      recordedRef.current = next;
      onUnvoteRef.current();
      return;
    }

    if (next === recordedRef.current) {
      return;
    }

    recordedRef.current = next;
    hasSavedRef.current = true;
    onVoteRef.current(next);
  }, []);

  const flushDebounced = useMemo(
    () => debounce((next) => flush(next), 750),
    [flush]
  );

  useEffect(() => () => flushDebounced.cancel(), [flushDebounced]);

  return (
    <div className="assessment-question row">
      <div className="col">
        <h2 className="question-title">
          {children}
          {question.text}
        </h2>
        {isMarker ? (
          <div className="question-comment">
            <textarea
              className="form-control"
              value={value}
              rows={5}
              aria-label={question.text}
              onChange={(e) => {
                const next = e.target.value;
                setValue(next);
                flushDebounced(next);
              }}
              onBlur={(e) => {
                flushDebounced.cancel();
                flush(e.target.value);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

const Question = ({
  question,
  children,
  response,
  onVote,
  onUnvote = () => {},
  isMarker = true,
}) => {
  const questionType = resolveLiveQuestionType(question);
  const isSentiment = isLiveSentimentQuestion(question);
  const isMultiSelect = questionType === 'multi-select';
  const radioName = `live-q-${question?.id ?? question?.guid}`;

  const handleVote = (e, value) => {
    try {
      e.target.blur();
    } catch (e) {
      // suppress failures
    }
    onVote(value);
  };

  const selectedValues = useMemo(
    () => new Set(selectedLiveChoiceValues(question, response)),
    [question, response]
  );
  const naChoice = useMemo(() => liveNaChoice(question), [question]);
  const naChosen = Boolean(response?.is_na);

  const options = useMemo(() => {
    if (!question || questionType === 'long-text') {
      return [];
    }

    if (isSentiment) {
      // `decoratedOptions` ranks by value ascending so the lowest value always
      // gets the most negative decoration. The list is then reversed for
      // display, which preserves the long-standing highest-first order.
      return decoratedOptions(question)
        .map((option) => ({
          ...option,
          id: option.id ?? option.guid,
          classNames: {
            [PERFORMING_CLASS[option.bucket]]: !!option.bucket,
            chosen: !naChosen && sameValue(response?.value, option.value),
            'not-chosen':
              response &&
              (naChosen || !sameValue(response.value, option.value)),
          },
        }))
        .reverse();
    }

    return orderedLiveChoices(question).map((option) => {
      const valueKey = String(option.value);
      const chosen = !naChosen && selectedValues.has(valueKey);
      return {
        ...option,
        id: option.id ?? option.guid,
        classNames: {
          chosen,
          'not-chosen': response && !chosen,
          'question-response-plain': true,
          'custom-radio': !isMultiSelect,
          'custom-checkbox': isMultiSelect,
        },
      };
    });
  }, [
    question,
    questionType,
    response,
    isSentiment,
    isMultiSelect,
    selectedValues,
    naChosen,
  ]);

  const handleMultiToggle = (value) => {
    const next = new Set(selectedValues);
    const key = String(value);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }

    const serialized = orderedLiveChoices(question)
      .map((option) => String(option.value))
      .filter((optionValue) => next.has(optionValue))
      .join(FIELD_DELIMITER);

    onVote(serialized);
  };

  if (!question) {
    return null;
  }

  if (questionType === 'long-text') {
    return (
      <LiveComment
        key={question.id ?? question.guid}
        question={question}
        response={response}
        onVote={onVote}
        onUnvote={onUnvote}
        isMarker={isMarker}
      >
        {children}
      </LiveComment>
    );
  }

  return (
    <div className="assessment-question row">
      <div className="col">
        <h2 className="question-title">
          {children}
          {question.text}
        </h2>
        <div
          className="question-responses list-unstyled"
          {...(isSentiment
            ? {}
            : {
                role: isMultiSelect ? 'group' : 'radiogroup',
                'aria-label': question.text,
              })}
        >
          {options.map((option) => {
            const {
              id,
              text,
              value,
              classNames,
              icon,
              rotate,
              shrink,
              color,
              ink,
              colorOnly,
            } = option;
            const chosen = isMultiSelect
              ? !naChosen && selectedValues.has(String(value))
              : !naChosen && sameValue(response?.value, value);
            const style = isSentiment
              ? { '--response-color': color, '--response-ink': ink }
              : { '--response-color': SINGLE_SELECT_COLOR };

            if (isSentiment) {
              const Tag = isMarker ? 'button' : 'div';
              return (
                <Tag
                  key={id}
                  className={cn('question-response', classNames)}
                  style={style}
                  {...(isMarker
                    ? {
                        type: 'button',
                        onMouseDown: preventDefault,
                        onClick: (e) => handleVote(e, value),
                      }
                    : {})}
                >
                  <SentimentMarker
                    icon={icon}
                    rotate={rotate}
                    shrink={shrink}
                    colorOnly={colorOnly}
                    className="response-icon"
                  />
                  <div>{text}</div>
                </Tag>
              );
            }

            const Tag = isMarker ? 'label' : 'div';
            return (
              <Tag
                key={id}
                className={cn('question-response', classNames)}
                style={style}
              >
                {isMarker ? (
                  <input
                    type={isMultiSelect ? 'checkbox' : 'radio'}
                    name={radioName}
                    value={value}
                    checked={chosen}
                    onChange={() =>
                      isMultiSelect ? handleMultiToggle(value) : onVote(value)
                    }
                  />
                ) : null}
                {isMultiSelect ? <ResponseCheck /> : <ResponseRadio />}
                <div>{text}</div>
              </Tag>
            );
          })}
          {naChoice ? (
            <NaOption
              naChoice={naChoice}
              naChosen={naChosen}
              response={response}
              isMarker={isMarker}
              isSentiment={isSentiment}
              radioName={radioName}
              onVote={onVote}
              handleVote={handleVote}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Question;
